






















import { Vue, Component, Mixins, Prop, Watch } from "vue-property-decorator";

import CommitteeAdminPanel from "@share/committees/committee-form/components/the-admin-panel/CommitteeAdminPanel.vue";
import Step1Information from "@share/committees/committee-form/steps/step-1-information/Step1Information.vue";
import Step2TicketsSelect from "@share/committees/committee-form/steps/step-2-tickets-select/Step2TicketsSelect.vue";
import DateTimeControl from "@share/common/form/form-controls/date/date-time/DateTimeControl.vue";
import Loadable from "@share/common/service-components/loadable/Loadable.vue";

import { StepView } from "@share/committees/committee-form/types";
import { CommitteeFormService } from "@share/committees/committee-form/services/CommitteeFormService";

import { CommitteeFormModel, CommitteeFormModelInterface } from "@share/committees/committee-form/types/CommitteeFormModel";
import { getValidationObject } from "@share/committees/committee-form/data";
import { validationMixin } from "vuelidate";
import { Modal } from "@common/Modal";
import { repositories } from "@service/api/Repositories";
import { RepositoryFabric } from "@api/repositories/RepositoryFabric";
import { CommitteeModelInterface } from "@api/models/interfaces/committees/CommitteeModelInterface";
import { UserProfileService } from "@share/common/services/dataLoading/userProfiles/UserProfilesService";
import { CommitteeModelMapper } from "@share/committees/committee-form/helpers/CommitteeModelMapper";
import { Notice } from "@service/components/toast/type/Notice";
import { noticeActions } from "@service/store/notice/actions";
import { NoticeLevel } from "@service/components/toast/type/Enums";
import Resetable from "@share/common/service-components/form/Resetable.vue";

import { TicketRowInterfaceForm } from "@share/committees/common/types/interfaces/TicketsRowInterface";

@Component({
    components: { CommitteeAdminPanel, Step1Information, Step2TicketsSelect },
    validations() {
        return getValidationObject();
    }
})

export default class CommitteeForm extends Mixins(validationMixin, Loadable, Resetable) {
    @Prop() committee: CommitteeModelInterface | null;

    currentStep: StepView = StepView.information;

    StepView = StepView;
    ticketValidationError = false;

    formModel: CommitteeFormModelInterface =  Vue.observable(new CommitteeFormModel());

    /**
     * service for loading profiles
     */
    userProfileService: UserProfileService | null = null;

    /**
     * service for backend interaction
     */
    formService: CommitteeFormService = new CommitteeFormService(
        repositories.get(RepositoryFabric.OBJECT)
    );

    async created() {
        if(this.committee || this.$route.params.id) {
            this.initFormForEdit(this.$route.params.id, this.committee);
        }
    }

    async initFormForEdit(committeeId: string, existingCommittee: CommitteeModelInterface | null) {

        this.userProfileService = new UserProfileService(
            this.$repositories.get(RepositoryFabric.USER),
            ["id", "userId", "lastName", "firstName", "photoFileId", "direction"]
        );

        try {
            this.isLoading = true;

            let committee, profiles;

            //it might be replace to form service
            if(existingCommittee?.id) {
                [committee, profiles] = await Promise.all([
                    (await this.formService.getCommittee(existingCommittee.id))[0],
                    this.userProfileService.getUserProfiles([...existingCommittee.userIds, existingCommittee.secretary])
                ]);
            }
            else {
                committee = (await this.formService.getCommittee(committeeId))[0];
                profiles = await this.userProfileService.getUserProfiles([...committee.userIds, committee.secretary]);
            }

            this.formModel = CommitteeModelMapper.toForm(committee, profiles);
        }
        catch(e) {
            this.$store.dispatch('reportError', e);
        }
        finally {
            this.isLoading = false;
        }
    }

    updateSelectedTickets(value: string[]) {
        this.formModel && (this.formModel.ticketIds = value);
    }

    /**
     * saving the committee
     */
    async save() {
        try {
            this.ticketValidationError && (this.ticketValidationError = false);

            const secondStepIsValid = this.validateSecondStep()

            if(secondStepIsValid && this.formModel) {
                const committee = await this.formService.save(this.formModel);

                if(committee) {
                    this.unloadService?.unsubscribe();

                    const notice: Notice = {
                        text: this.$t("committees.form.messages.save").toString(),
                        level: NoticeLevel.success
                    }

                    this.$store.dispatch(noticeActions.submitNotice, notice);

                    this.close()
                }
            }
        }
        catch(e) {
            this.$store.dispatch('reportError', e);

            this.$store.dispatch('reportException', e, { root: true });
            const notice: Notice = {
                text: this.$t("committees.form.messages.failedSave").toString(),
                level: NoticeLevel.danger
            }

            this.$store.dispatch(noticeActions.submitNotice, notice);
        }
    }

    nextStep() {
        if(this.currentStep === StepView.information) {
            const formIsValid = this.validateFirstStep();

            if(!formIsValid) {
                return;
            }
        }

        this.currentStep++;
    }

    back() {
        if(this.currentStep === StepView.information) {
            this.close();
        }

        if(this.currentStep > 1) {
            this.currentStep--;
        }
    }

    @Watch('formModel', { deep: true })
    watchFormModel() {
        this.resetable = !!this.formModel?.needReset;
    }

    validateFirstStep() {
        this.touch();

        const dateTimeControl = this.getDateTimeControl();
        return !this.$v.$invalid && !(dateTimeControl && dateTimeControl.$v.$invalid);
    }

    openTicket(ticket: TicketRowInterfaceForm) {
        this.$emit('openTicket', ticket, this.formModel.subjectType.id);
    }

    validateSecondStep() {

        const firstStepIsValid = this.validateFirstStep();
        let isValid = true;

        if(!firstStepIsValid) {
            this.back();
            isValid = false;
        }
        else if(!this.formModel?.ticketIds?.length) {
            this.ticketValidationError = true;
            this.showTicketValidationErrorModal();
            isValid = false;
        }

        return isValid;
    }

    touch() {
        this.$v.$touch();
        this.getDateTimeControl()?.validate();
    }

    close() {
        this.$emit('close');
    }

    getDateTimeControl() {
        const formComponent = (this.$refs.form as Step1Information | undefined)
        return (formComponent?.$refs["dateTimeControl"] as DateTimeControl | undefined);
    }

    async showTicketValidationErrorModal() {
        const modal = new Modal({
            title: "Error",
            contentText: this.$t("committees.form.errors.tickets.message", { subject: this.$t(`committees.form.errors.tickets.${this.formModel?.subjectType.id}`)}).toString(),
            buttons: [
                {
                    text: 'Ok',
                    class: 'is-green is-middle',
                    returnValue: true
                }
            ]
        });

        return await modal.showConfirm();
    }

    get greenButtonDisabled() {
        return false
    }

    get isEdit() {
        return this.committee || this.$route.params.id;
    }

    beforeDestroy() {
        this.unloadService?.unsubscribe();
    }
}

